import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const { userProfile, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
  };

  // Filter navigation items based on user role
  const getNavigationItems = () => {
    const allItems = [
      { name: 'Tổng quan', href: '/dashboard', icon: 'ri-dashboard-line' },
      { name: 'Sản phẩm', href: '/products', icon: 'ri-shopping-bag-line' },
      { name: 'Đơn hàng', href: '/orders', icon: 'ri-file-list-line' },
      { name: 'Quản lý kho', href: '/inventory', icon: 'ri-archive-line' },
      { name: 'Đối tác', href: '/partners', icon: 'ri-team-line' },
      { name: 'Báo cáo', href: '/reports', icon: 'ri-bar-chart-line' },
      { name: 'Người dùng', href: '/users', icon: 'ri-user-line' },
    ];

    // Partner role can only access Products, Orders, and Reports
    if (userProfile?.role === 'partner') {
      return allItems.filter(item => 
        item.href === '/products' || 
        item.href === '/orders' || 
        item.href === '/reports'
      );
    }

    // Manager and admin can access all items including Users
    return allItems;
  };

  const navigationItems = getNavigationItems();

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 sm:w-72 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:static lg:inset-0 flex flex-col`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-14 sm:h-16 px-4 sm:px-6 border-b border-gray-200 flex-shrink-0">
          <img 
            src="https://static.readdy.ai/image/e98866bb21555bc01871226869278e9c/d81c89946136bef8b2a90450a4ebf256.png" 
            alt="Navik Pro Sale" 
            className="h-6 sm:h-8 w-auto"
            style={{ maxWidth: 'none' }}
          />
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden p-2 text-gray-500 hover:text-black rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-lg sm:text-xl"></i>
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 mt-4 sm:mt-6 px-2 sm:px-3 overflow-y-auto">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center px-3 py-3 mb-1 sm:mb-2 rounded-lg transition-all duration-200 whitespace-nowrap ${
                  isActive
                    ? 'bg-orange-50 text-orange-600 border-r-4 border-orange-500 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-black'
                }`}
              >
                <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className={`${item.icon} text-base sm:text-lg`}></i>
                </div>
                <span className="font-medium text-sm sm:text-base">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Info & Logout - Fixed at bottom */}
        <div className="flex-shrink-0 p-3 sm:p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center mb-3 sm:mb-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-gray-600 text-sm sm:text-base"></i>
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-black truncate">
                {userProfile?.name || userProfile?.email}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {userProfile?.role === 'manager' ? 'Quản lý' : 'Đối tác'}
              </p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center px-3 py-2 text-xs sm:text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap"
          >
            <i className="ri-logout-box-line mr-2 flex-shrink-0 text-sm"></i>
            <span>Đăng xuất</span>
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-0">
        {/* Top header */}
        <header className="bg-white border-b border-gray-200 h-14 sm:h-16 flex-shrink-0 z-30">
          <div className="flex items-center justify-between px-4 sm:px-6 h-full">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 text-gray-500 hover:text-black hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i className="ri-menu-line text-lg sm:text-xl"></i>
            </button>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button className="p-2 text-gray-500 hover:text-black hover:bg-gray-100 rounded-lg transition-colors">
                <i className="ri-notification-line text-lg sm:text-xl"></i>
              </button>
              <button className="p-2 text-gray-500 hover:text-black hover:bg-gray-100 rounded-lg transition-colors">
                <i className="ri-settings-line text-lg sm:text-xl"></i>
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-3 sm:p-4 lg:p-6 bg-gray-50 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
